import { Root, Trigger } from '@radix-ui/react-dropdown-menu';
import { Ellipsis } from 'lucide-react';
import * as React from 'react';

import { DimeButton, type DimeButtonProps } from './DimeButton.js';
import { DimeDropdownMenuContent, DimeDropdownMenuItem } from './DimeDropdownMenu.js';
import { useDisabledTooltip } from './lib/use-disabled-tooltip.js';
import { cn } from './lib/utils.js';

interface DimeRowActionsProps {
  children: React.ReactNode;
  label?: string;
  align?: 'start' | 'center' | 'end';
  className?: string;
  variant?: DimeButtonProps['variant'];
  size?: DimeButtonProps['size'];
  icon?: React.ReactNode;
}

export function DimeRowActions({
  children,
  label = 'Open menu',
  align = 'end',
  className,
  variant = 'ghost',
  size = 'icon-xs',
  icon,
}: Readonly<DimeRowActionsProps>) {
  return (
    <Root>
      <Trigger asChild>
        <DimeButton
          variant={variant}
          size={size}
          className={className}
          aria-label={label}
          onClick={(event) => event.stopPropagation()}
        >
          <span className="sr-only">{label}</span>
          {icon ?? <Ellipsis />}
        </DimeButton>
      </Trigger>
      <DimeDropdownMenuContent
        align={align}
        className="w-40"
        onCloseAutoFocus={keepFocusAnItemMoved}
      >
        {children}
      </DimeDropdownMenuContent>
    </Root>
  );
}

// Refocusing the trigger would blur a field in the dialog an item opened.
function keepFocusAnItemMoved(event: Event) {
  const focused = document.activeElement;
  if (focused && focused !== document.body) event.preventDefault();
}

// WHY: layered via className (DimeButton's warning pattern) with `!` markers to beat the base
// item's equal-specificity `focus:**:text-accent-foreground`, since DimeDropdownMenuItem itself only knows 'default'/'destructive'.
const WARNING_CLASS_NAME =
  'text-amber-700! focus:bg-amber-500/10! focus:text-amber-700! focus:**:text-amber-700! dark:text-amber-300! dark:focus:text-amber-300! dark:focus:bg-amber-500/20! dark:focus:**:text-amber-300!';

interface DimeRowActionsItemProps extends React.ComponentProps<typeof DimeDropdownMenuItem> {
  destructive?: boolean;
  warning?: boolean;
  // Shown in a tooltip when the item is disabled — e.g. explaining a missing permission.
  // A disabled item has pointer-events-none and is removed from Radix's roving tab stops, so
  // the tooltip trigger can't live on the item itself; wrap in a non-disabled span instead.
  disabledTooltip?: string;
}

export function DimeRowActionsItem({
  destructive = false,
  warning = false,
  disabledTooltip,
  disabled,
  className,
  ...props
}: Readonly<DimeRowActionsItemProps>) {
  const item = (
    <DimeDropdownMenuItem
      variant={destructive ? 'destructive' : 'default'}
      disabled={disabled}
      className={warning ? cn(WARNING_CLASS_NAME, className) : className}
      {...props}
    />
  );

  return useDisabledTooltip({ disabled, tooltip: disabledTooltip }, item);
}
